import styles from './styles.scss';
import { rm, NO_ELEMENT, scrollToTarget } from 'utils';
import { reducer, isValid, otherQuestionsInitialState } from './OtherQuestionsHelper';
import { ChangeEvent, useEffect, useReducer, useRef } from 'react';
import { useToggle } from 'hooks';
import { postSendOtherQuestion } from 'services/ApiCustomerSupportFormService';
import { TextArea } from 'components/Shared/Form';
import { Button, LoaderButton } from 'components/Button';
import { IBasicResponse } from 'models/IBasicResponse';
import {
  BlueSectionContainer,
  ErrorContainer,
  SubmitContainer
} from 'components/CustomerSupportForms/Components/SectionContainer/SectionContainer';
import { useDispatch } from 'react-redux';
import { GtmCustomerSupportFormSubmit } from 'store/googleTagManager/actions';
import { FormType } from 'models/FormType';
import { CustomerContact } from '../Components/CustomerContact/CustomerContact';
import { contactOptionChanged, OrderFormProps } from '../Utils';

const QUESTION_MAX_LENGTH = 1000;

export const OtherQuestionsForm = ({ order, error, returnUrl, onSuccess }: OrderFormProps) => {
  const anchorRef = useRef(NO_ELEMENT);
  const scrollToTopIfNeeded = scrollToTarget(anchorRef);
  const [internalState, internalDispatch] = useReducer(reducer, otherQuestionsInitialState);
  const [loading, toggleLoading] = useToggle(false);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (order) {
      internalDispatch({ type: 'address', value: order?.deliveryAddress });
      internalDispatch({ type: 'email', value: order?.email });
    }
    if (error) {
      internalDispatch({
        type: 'hasError',
        value: true
      });
    }
  }, [error, order]);

  const handleQuestionInput = (_event: ChangeEvent<HTMLTextAreaElement>, newValue: string) => {
    internalDispatch({
      type: 'question',
      value: newValue
    });
    internalDispatch({
      type: 'questionError',
      value: ''
    });
  };
  const { email, address, question, contactOption } = internalState;

  const completeInternal = () => {
    toggleLoading();
    internalDispatch({ type: 'generalError', value: '' });
    if (isValid(internalState, internalDispatch)) {
      if (email && order && address) {
        return postSendOtherQuestion({
          email,
          orderNumber: order.orderNumber,
          question,
          hash: order.hash,
          customerContactOption: contactOption,
          address
        })
          .then(() => {
            toggleLoading();
            onSuccess();
            scrollToTopIfNeeded();
            reduxDispatch(GtmCustomerSupportFormSubmit(FormType.OtherQuestions));
          })
          .catch((response: IBasicResponse) => {
            toggleLoading();
            internalDispatch({ type: 'generalError', value: response.message[0] });
          });
      }
    }

    toggleLoading();
    return Promise.resolve();
  };

  return (
    <>
      <h3>{rm('customerpage_other_questions_question')}</h3>
      <BlueSectionContainer error={internalState.questionError}>
        <span>{rm('Customerpage_Other_Questions_Subheader')}</span>
        <TextArea
          onChange={handleQuestionInput}
          textlimit={QUESTION_MAX_LENGTH}
          value={internalState.question}
        />
        <p
          id={styles.returnMsgLength}
        >{`(${internalState.question.length}/${QUESTION_MAX_LENGTH})`}</p>
      </BlueSectionContainer>
      {internalState.questionError && (
        <ErrorContainer>{internalState.questionError}</ErrorContainer>
      )}
      <CustomerContact
        contact={internalState.contactOption}
        onChange={val => contactOptionChanged(val, internalDispatch)}
        error={internalState.contactOptionError}
      />
      <SubmitContainer>
        <LoaderButton appearance="whitePillPrimary" onClick={completeInternal} loading={loading}>
          {loading ? '' : rm('customerpage_returns_submit')}
        </LoaderButton>
        <Button appearance="whitePill" href={returnUrl}>
          {rm('customerpage_returns_cancel')}
        </Button>
      </SubmitContainer>
      {internalState.generalError && (
        <BlueSectionContainer error>{internalState.generalError}</BlueSectionContainer>
      )}
    </>
  );
};
